/**
 * Billing-event analytics taxonomy (growth epic G1-2).
 *
 * A single, consistent vocabulary for the Stripe-webhook billing lifecycle so
 * PostHog funnels/retention can be built on stable, documented event names
 * instead of the ad-hoc legacy trio (`plan_upgraded` / `plan_cancelled` /
 * `checkout_abandoned`). The webhook (`app/api/stripe/webhook/route.ts`) emits
 * these via the `trackBillingEvent` server helper (`lib/billing-events.ts`),
 * which injects the `environment` discriminator and forwards the PostHog
 * "account" group + `classification` for production-only slicing.
 *
 * This module is wire-only (no Prisma / Next / Node imports) so it can be shared
 * by web and mobile. The `classification` enum mirrors the prisma
 * `AccountClassification` enum (see prisma/schema.prisma) WITHOUT importing
 * `@prisma/client` — the contracts package is deliberately prisma-free.
 */
import { z } from "zod";

/**
 * Canonical billing-event names. Add new lifecycle events here so every emitter
 * shares one vocabulary; the value is the literal PostHog event name.
 */
export const BILLING_EVENTS = {
  checkoutCompleted: "checkout_completed",
  subscriptionTrialStarted: "subscription_trial_started",
  subscriptionActivated: "subscription_activated",
  invoicePaid: "invoice_paid",
  paymentFailed: "payment_failed",
  paymentRecovered: "payment_recovered",
  paymentRefunded: "payment_refunded",
  subscriptionCancelScheduled: "subscription_cancel_scheduled",
  subscriptionCancelled: "subscription_cancelled",
  planChanged: "plan_changed",
  planDowngraded: "plan_downgraded",
  planReactivated: "plan_reactivated",
} as const;

export type BillingEventName = (typeof BILLING_EVENTS)[keyof typeof BILLING_EVENTS];

/**
 * Pre-taxonomy events kept for funnel continuity. These still fire from the
 * webhook (so historical dashboards/funnels don't break) but are superseded by
 * the canonical {@link BILLING_EVENTS} above.
 *
 * @deprecated superseded by BILLING_EVENTS; kept for funnel continuity.
 */
export const LEGACY_BILLING_EVENTS = {
  planUpgraded: "plan_upgraded",
  planCancelled: "plan_cancelled",
  checkoutAbandoned: "checkout_abandoned",
} as const;

export type LegacyBillingEventName =
  (typeof LEGACY_BILLING_EVENTS)[keyof typeof LEGACY_BILLING_EVENTS];

/**
 * Account classification, mirrored from the prisma `AccountClassification` enum.
 * Carried on every billing event so PostHog reporting can exclude
 * INTERNAL/TEST/STAGING/DEMO activity (epic G0-3).
 */
export const BILLING_EVENT_CLASSIFICATIONS = [
  "PRODUCTION",
  "INTERNAL",
  "TEST",
  "STAGING",
  "DEMO",
] as const;

/**
 * What an `invoice.payment_failed` did to the account's dunning state — the
 * `dunningOutcome` property of `payment_failed`:
 *  - `armed` — the episode was opened (or an open one continued);
 *  - `concurrent_change` — the dunning clock, or the plan / recorded
 *    subscription the decision rested on, changed during handling: stood down;
 *  - `one_off_invoice` — not a subscription invoice, so dunning never applies;
 *  - `not_current_subscription` / `site_control_protected` — the failed
 *    subscription does not drive the account's billing (a leftover beside the
 *    recorded one, or a non-Site-Control subscription on a Site Control
 *    account);
 *  - `not_on_paid_plan` — the account is not on a paid plan (e.g. a Site
 *    Control prospect whose hosted-invoice attempt was declined), so nothing
 *    is armed and nothing is cleared;
 *  - `site_control_pending_activation` — the failed subscription is a Site
 *    Control one but the account is not on Site Control yet: a prospect on the
 *    invoice path (free, or comped onto another paid tier) whose hosted
 *    invoice attempt was declined. The grant is deferred to payment, so
 *    arming would dun an account for a product it does not have;
 *  - `invoice_settled` / `subscription_ended` / `invoice_missing` — handled
 *    after the invoice was paid or voided, after its subscription ended, or
 *    for an invoice Stripe no longer knows: nothing left to dun.
 */
export const DUNNING_OUTCOMES = [
  "armed",
  "concurrent_change",
  "one_off_invoice",
  "not_current_subscription",
  "site_control_protected",
  "not_on_paid_plan",
  "site_control_pending_activation",
  "invoice_settled",
  "subscription_ended",
  "invoice_missing",
] as const;

export type DunningOutcome = (typeof DUNNING_OUTCOMES)[number];

/**
 * Base properties shared by every billing event. All fields are optional except
 * `stripeEventId` (always present, for traceability back to the Stripe event —
 * webhook-level idempotency means each event is processed once, so no per-emit
 * dedup is needed). Individual emitters set only the fields relevant to that
 * event; the schema is intentionally permissive so a single shape covers the
 * whole taxonomy.
 */
export const billingEventPropsSchema = z.object({
  /** Stripe `event.id` — joins the PostHog event back to the source Stripe event. */
  stripeEventId: z.string(),
  /** Resolved plan after the event (e.g. "business", "site_control"). */
  plan: z.string().optional(),
  /** Canonical billing Account id (also carried as the PostHog account group). */
  accountId: z.string().optional(),
  /** Plan before the event — set on plan-transition events. */
  previousPlan: z.string().optional(),
  /** Account classification (production-only reporting filter, epic G0-3). */
  classification: z.enum(BILLING_EVENT_CLASSIFICATIONS).optional(),
  /** Deployment environment ("production" / "staging" / …) — injected by the emitter. */
  environment: z.string().optional(),
  /** Monthly recurring revenue in GBP (annual subs normalized to 1/12). */
  mrrGbp: z.number().optional(),
  /** A one-off / invoice amount in GBP (pounds). */
  amountGbp: z.number().optional(),
  /** Raw Stripe `amount_paid` (pence) when carried verbatim. */
  amountPaid: z.number().optional(),
  /** ISO currency code, uppercased (e.g. "GBP"). */
  currency: z.string().optional(),
  /** Stripe invoice `billing_reason` (e.g. "subscription_create"). */
  billingReason: z.string().optional(),
  /** Stripe subscription id. */
  subscriptionId: z.string().optional(),
  /** Stripe invoice id. */
  invoiceId: z.string().optional(),
  /** Stripe charge id. */
  chargeId: z.string().optional(),
  /** Stripe price id. */
  priceId: z.string().optional(),
  /** Stripe Checkout Session id for started/completed funnel joins. */
  checkoutSessionId: z.string().optional(),
  /** Authoritative Checkout total in pence. */
  amountPence: z.number().optional(),
  /** Product billing cadence (for example monthly or annual). */
  billingCycle: z.string().optional(),
  /** Stripe Checkout mode (`payment` or `subscription`). */
  checkoutMode: z.string().optional(),
  /** Product-level checkout classification. */
  checkoutType: z.string().optional(),
  /** Whether the checkout included the setup-service add-on. */
  withSetupService: z.boolean().optional(),
  /** Stable instrumentation boundary that emitted the event. */
  source: z.string().optional(),
  /** ISO timestamp the trial ends. */
  trialEndsAt: z.string().optional(),
  /** ISO timestamp the current period ends (cancel-scheduled). */
  periodEndsAt: z.string().optional(),
  /** Subscription lifetime in days (cancellation). */
  daysActive: z.number().optional(),
  /** True when the cancelled/lapsed subscription was still trialing. */
  wasTrialing: z.boolean().optional(),
  /**
   * Stripe PaymentIntent `last_payment_error.decline_code` on a failed payment
   * (e.g. "insufficient_funds", "card_declined") — the issuer's categorical
   * reason. Absent when the decline could not be resolved at event time.
   */
  declineCode: z.string().optional(),
  /** Stripe PaymentIntent `last_payment_error.code` (e.g. "card_declined"). */
  errorCode: z.string().optional(),
  /** Human-readable Stripe decline message, truncated. Not PII — issuer copy. */
  errorMessage: z.string().optional(),
  /** `payment_failed` only: see {@link DUNNING_OUTCOMES}. */
  dunningOutcome: z.enum(DUNNING_OUTCOMES).optional(),
});

export type BillingEventProps = z.infer<typeof billingEventPropsSchema>;
